import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import PageHeader from '@/components/PageHeader';
import { BREATHING_EXERCISES } from '@/config/breathingExercises';
import PremiumUpsellBreathing from './PremiumUpsellBreathing';

const BreathingPremium = ({ t }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEnglish = t?.locale === 'en-US';
  const isFreeUser = !user?.isPremium && user?.subscription !== 'premium' && user?.role !== 'admin';

  const handleExerciseClick = (exercise) => {
    // Free users can only access non-premium exercises
    if (isFreeUser && exercise.premium) {
      navigate('/premium');
      return;
    }
    navigate(`/breathing-exercise/${exercise.id}`);
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] px-4 md:px-8 pb-32 relative overflow-hidden" style={{ paddingTop: '100px' }}>
      {/* Background effects */}
      <div className="absolute -top-32 -left-20 w-[320px] h-[320px] bg-[#6F42EA] opacity-[0.12] blur-[130px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-[-40px] w-[280px] h-[280px] bg-[#3B48EE] opacity-[0.10] blur-[140px] rounded-full pointer-events-none" />

      <div className="max-w-4xl mx-auto relative z-10">
        <PageHeader t={t} />

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-3">
            {isEnglish ? 'Advanced Breathing Exercises' : 'Exercices de Respiration Avancée'}
          </h1>
          <p className="text-white/70 text-lg mb-2">
            {isEnglish ? 'Master your breath. Calm your mind.' : 'Maîtrisez votre souffle. Apaisez votre esprit.'}
          </p>
          <p className="text-white/50 text-xs italic">
            {isEnglish ? 'Not counted as soundscapes' : 'Non compté comme écoute'}
          </p>
        </div>

        {/* Exercises grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-32">
          {BREATHING_EXERCISES.map((exercise) => (
            <button
              key={exercise.id}
              onClick={() => handleExerciseClick(exercise)}
              disabled={false}
              className="relative group"
            >
              <div
                className={`rounded-2xl p-6 backdrop-blur-md border transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed ${
                  isFreeUser && exercise.premium ? 'opacity-60' : ''
                }`}
                style={{
                  borderColor: 'rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 4px 20px rgba(111, 66, 234, 0.2)',
                }}
              >
                {/* Halo effect on hover */}
                {/* Halo effect on hover */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle at center, rgba(111, 66, 234, 0.15) 0%, transparent 70%)',
                 filter: 'blur(20px)',
                }}
              />

                <div className="relative">
                  {/* Icon + Lock */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-4xl">{exercise.icon}</div>
                    {isFreeUser && exercise.premium && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-amber-300/80 font-medium">Premium</span>
                        <Lock className="w-4 h-4 text-amber-300/80" />
                      </div>
                    )}
                  </div>

                  {/* Name */}
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {isEnglish ? exercise.nameEn : exercise.name}
                  </h3>

                  {/* Rhythm - Improved readability */}
                  <p className="text-[#8B7AE6] font-mono text-base font-medium mb-2">{exercise.rhythm}</p>

                  {/* Description */}
                  <p className="text-white/70 text-sm mb-3">
                  </p>

                  {/* Duration badge - Updated wording */}
                  {!exercise.comingSoon && (
                    <div className="inline-block px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs">
                      {isEnglish ? 'Default duration: 3 min' : 'Durée par défaut : 3 min'}
                    </div>
                  )}

                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BreathingPremium;
